'use client';

import { useEffect } from 'react';

import { useTenantStore } from '@/stores/tenant-store';

import { startSessionReplay } from './analytics';

/**
 * On-demand PostHog session replay for the donation / registration → checkout
 * journey, gated per tenant via env so it can be turned off without code
 * changes.
 *
 * Recording is globally off (`disable_session_recording` in
 * instrumentation-client). We start it at an *entry point* — the donate modal
 * opening or the registration flow mounting — and because recording persists
 * across client-side navigation, the whole journey through `/donate/checkout`
 * is captured. This is a temporary diagnostic aid for checkout failures that
 * are invisible to server logs (e.g. a Payment Element that never mounts).
 *
 * Enable by listing tenant slugs in `NEXT_PUBLIC_POSTHOG_REPLAY_TENANTS`
 * (comma-separated, e.g. `uwr` or `uwr,dgr`). Empty / unset = off everywhere.
 * NEXT_PUBLIC_* is build-time inlined, so toggling requires a redeploy.
 */
const REPLAY_TENANT_SLUGS: ReadonlySet<string> = new Set(
  (process.env.NEXT_PUBLIC_POSTHOG_REPLAY_TENANTS ?? '')
    .split(',')
    .map((slug) => slug.trim().toLowerCase())
    .filter(Boolean),
);

/** Pure gate: is session replay enabled for this tenant slug? */
export function isSessionReplayEnabled(slug: string | null | undefined): boolean {
  return slug != null && REPLAY_TENANT_SLUGS.has(slug.toLowerCase());
}

/**
 * Starts session replay once when `active` becomes true for an enabled tenant.
 * Call at a flow entry point with the relevant condition — `open` for the
 * donate modal, `true` on registration mount. No-ops for disabled tenants.
 */
export function useSessionReplay(active: boolean): void {
  const slug = useTenantStore((state) => state.slug);
  const enabled = active && isSessionReplayEnabled(slug);

  useEffect(() => {
    if (!enabled) return;
    // No stop: let the entry-point → checkout journey record through to the end.
    // Turn off by removing the tenant from NEXT_PUBLIC_POSTHOG_REPLAY_TENANTS.
    startSessionReplay();
  }, [enabled]);
}
